/**
 * Type Guards for SIP Sessions
 *
 * Provides type-safe runtime checks for SIP.js session types.
 * These guards replace 'in' operator checks with proper TypeScript narrowing.
 */

import type { CallSipSession } from 'Modules/softphone/types';
import { createLogger } from 'Modules/softphone/utils/logger';
import { Invitation, Inviter } from 'sip.js';
import type { RootStore } from 'Stores/RootStore';
import { createSoftphoneCoordinatorStore } from '../store/coordinator';

const logger = createLogger('Softphone:Guards');

/**
 * Interface for sessions that support hold/unhold operations.
 * Both Inviter and Invitation sessions support these methods when established.
 */
export interface SessionWithHoldCapability {
  hold: () => Promise<void>;
  unhold: () => Promise<void>;
}

/**
 * Type guard to check if a session is an outbound call session.
 *
 * Outbound sessions are created when making outbound calls.
 * They have the `invite()` method to initiate the call.
 *
 * @param session - The SIP session to check
 * @returns True if the session is an outbound call (Inviter)
 */
export const isOutboundSession = (
  session: CallSipSession | null
): session is Inviter => {
  return session !== null && session instanceof Inviter;
};

/**
 * Type guard to check if a session is an inbound call session.
 *
 * Inbound sessions are created when receiving inbound calls.
 * They have `accept()` and `reject()` methods to handle the incoming call.
 *
 * @param session - The SIP session to check
 * @returns True if the session is an inbound call (Invitation)
 */
export const isInboundSession = (
  session: CallSipSession | null
): session is Invitation => {
  return session !== null && session instanceof Invitation;
};

/**
 * Type guard to check if a session has hold/unhold capability.
 *
 * Both Inviter and Invitation sessions support hold/unhold when established.
 * This guard checks for the presence and type of these methods.
 *
 * @param session - The SIP session to check
 * @returns True if the session supports hold/unhold operations
 */
export const hasHoldCapability = (
  session: CallSipSession | null
): session is CallSipSession & SessionWithHoldCapability => {
  if (session === null) {
    return false;
  }

  return (
    'hold' in session &&
    typeof session.hold === 'function' &&
    'unhold' in session &&
    typeof session.unhold === 'function'
  );
};

/**
 * Check if prerequisites are met for showing inbound notification
 *
 * @param rootStore - Optional object with personStore and uiStore for accessing user settings
 * @returns True if all prerequisites are met, false otherwise
 *
 * NOTE: This function is part of the NEW softphone module and should NOT
 * use legacy PhoneStore. Settings should be managed within the softphone
 * module's own store when implemented.
 */
export function checkInboundPrerequisites(
  rootStore?: Pick<
    RootStore,
    'personStore' | 'uiStore'
  >
): boolean {
  // Check "Enable incoming calls" setting from coordinator store
  // Get personId from rootStore to access the correct per-user coordinator store
  const personId = rootStore?.personStore?.loggedInPersonId;
  if (personId && personId > 0) {
    const coordinatorStore = createSoftphoneCoordinatorStore(personId);
    const incomingCallsEnabled = coordinatorStore.getState().enableIncomingCalls;
    if (!incomingCallsEnabled) {
      logger.debug('Incoming calls disabled in settings');
      return false;
    }
  }

  // Check DND status from user presence (shared UiStore is OK to use)
  if (rootStore?.personStore && rootStore?.uiStore) {
    const personId = rootStore.personStore.loggedInPersonId;
    if (personId > 0) {
      const presenceStatus = rootStore.uiStore.selectPersonPresenceStatus(
        personId.toString()
      );
      const isDND = presenceStatus?.state === 'DoNotDisturb';
      if (isDND) {
        logger.debug('User is in DND status', { status: presenceStatus.state });
        return false;
      }
    }
  }

  // TODO: Implement ring strategy setting in softphone module
  // Ring strategy should determine if web/mobile ringing is enabled
  // For now, defaulting to true (allow web/mobile ringing)
  const ringStrategyAllowsWeb = true;
  if (!ringStrategyAllowsWeb) {
    logger.debug('Ring strategy does not allow web/mobile');
    return false;
  }

  return true;
}

/**
 * Check if call is video based on SDP
 */
export function isVideoCall(invitation: Invitation): boolean {
  const sdp = invitation.request.body;
  if (!sdp) return false;
  return sdp.includes('m=video');
}

/**
 * Type guard to check if a string is a valid DTMFDigit
 */
export const isDTMFDigit = <T extends string>(
  k: string,
  validKeys: readonly T[]
): k is T => {
  return validKeys.includes(k as T);
};
